/**
 * Production line shop label: browser print, 100mm × 56mm landscape.
 * Parity with legacy Angular `printHtmlContent` (raw HTML + @page size).
 */

import { formatGlassSideDisplay } from './formatGlassSideDisplay';

/** Woo drawing type_code values — see backend mapping_rules.WOO_DRAWING_TYPE_CODES */
const WOO_TYPE_CUT_HOLES = new Set([5, 6, 7, 9]); // drilled holes + cut-out shape codes
const WOO_TYPE_DRAWING = new Set([1, 2, 3, 4, 8]); // lead, duplex, georgian, rounded, generic Drawing

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function trimStr(v: unknown): string {
  if (v === null || v === undefined) return '';
  return String(v).trim();
}

export function normalizeWooDrawingsFromOrder(order: Record<string, unknown>): Array<{
  type_code?: number;
  label?: string;
  url?: string;
}> {
  const meta = order.metadata as Record<string, unknown> | undefined;
  const raw = meta?.woo_drawings;
  if (Array.isArray(raw)) {
    return raw.filter((d): d is { type_code?: number; label?: string; url?: string } => d != null && typeof d === 'object');
  }
  if (raw && typeof raw === 'object') {
    return [raw as { type_code?: number; label?: string; url?: string }];
  }
  return [];
}

export function filterActiveOrderImages(
  images: ReadonlyArray<Record<string, unknown>>
): Record<string, unknown>[] {
  return images.filter((i) => {
    if (i.deleted_at != null && i.deleted_at !== '') return false;
    return i.is_active !== false;
  });
}

/**
 * S3 uploads: `image_type === 'cutout'` → Cut Holes; other active drawing uploads → Drawing.
 * `feature === 'drilled_holes'` counts as Cut Holes (holes drawing).
 */
export function classifyS3ImageExtras(images: ReadonlyArray<Record<string, unknown>>): {
  hasDrawing: boolean;
  hasCutHoles: boolean;
} {
  const active = filterActiveOrderImages(images);
  let hasDrawing = false;
  let hasCutHoles = false;
  for (const img of active) {
    const imageType = trimStr(img.image_type).toLowerCase();
    const feature = trimStr(img.feature).toLowerCase();
    if (imageType === 'cutout' || feature === 'cutout' || feature === 'drilled_holes') {
      hasCutHoles = true;
      continue;
    }
    // Any other manual upload (typically image_type drawing + feature) counts as Drawing on the label
    hasDrawing = true;
  }
  return { hasDrawing, hasCutHoles };
}

export function classifyWooDrawingExtras(
  drawings: ReadonlyArray<{ type_code?: number; label?: string; url?: string }>
): { hasDrawing: boolean; hasCutHoles: boolean } {
  const withUrl = drawings.filter((d) => trimStr(d.url) !== '');
  let hasDrawing = false;
  let hasCutHoles = false;
  for (const d of withUrl) {
    const tc = d.type_code;
    if (tc === undefined || tc === null || Number.isNaN(Number(tc))) {
      hasDrawing = true;
      continue;
    }
    const n = Number(tc);
    if (WOO_TYPE_CUT_HOLES.has(n)) {
      hasCutHoles = true;
    } else if (WOO_TYPE_DRAWING.has(n)) {
      hasDrawing = true;
    } else {
      hasDrawing = true;
    }
  }
  return { hasDrawing, hasCutHoles };
}

function formatSpacerTriple(type: string, color: string, size: string): string {
  return `${type} -- ${color} -- ${size}`;
}

function displaySpacerPart(v: unknown): string {
  const s = trimStr(v);
  if (!s) return '';
  return s.replace(/_/g, ' ');
}

function hasLeadedSection(order: Record<string, unknown>): boolean {
  const pattern = trimStr(order.decorative_leaded_pattern);
  const color = trimStr(order.decorative_lead_color);
  const thick = order.decorative_lead_thickness_mm;
  const solder = order.decorative_lead_soldering_required === true;
  if (pattern && pattern !== '-') return true;
  if (color) return true;
  if (thick !== null && thick !== undefined && String(thick).trim() !== '') return true;
  return solder;
}

function buildFeatureStrings(order: Record<string, unknown>): string {
  const polishedValue = order.polished_edges === true ? 'Polished' : '';
  const dubbedCorners = order.dubbed_corners === true ? 'Dubbed Corners' : '';
  const backingFoil = order.aluminium_backing_foil === true ? 'Backing Foil' : '';
  const leadedValue = hasLeadedSection(order) ? 'Leaded' : '';
  const argValue = order.argon_filled === true ? 'Argon' : '';
  const georgianBarValue =
    trimStr(order.georgian_bar_type) || trimStr(order.georgian_bar_thickness) ? 'Georgian Bar' : '';
  const duplexTypeValue =
    trimStr(order.duplex_bar_type) || trimStr(order.duplex_bar_thickness) ? 'Duplex Type' : '';

  return [
    polishedValue,
    dubbedCorners,
    backingFoil,
    leadedValue,
    argValue,
    georgianBarValue,
    duplexTypeValue,
  ]
    .filter((f) => f !== '')
    .join(' , ');
}

function mergeImageExtras(
  features: string,
  s3: { hasDrawing: boolean; hasCutHoles: boolean },
  woo: { hasDrawing: boolean; hasCutHoles: boolean }
): { features: string; featuresImg: string } {
  const drawingValue = s3.hasDrawing || woo.hasDrawing ? 'Drawing' : '';
  const cutHoleValue = s3.hasCutHoles || woo.hasCutHoles ? 'Cut Holes' : '';
  const featuresImg = [drawingValue, cutHoleValue].filter((f) => f !== '').join(' , ');
  return { features, featuresImg };
}

/**
 * Build full HTML document for one label, or `null` if legacy rule says nothing to print.
 */
export function buildProductionLineLabelHtml(
  order: Record<string, unknown>,
  orderImages: ReadonlyArray<Record<string, unknown>>
): string | null {
  const wooList = normalizeWooDrawingsFromOrder(order).filter((d) => trimStr(d.url) !== '');
  const s3Class = classifyS3ImageExtras(orderImages);
  const wooClass = classifyWooDrawingExtras(wooList);
  const featBase = buildFeatureStrings(order);
  const { features, featuresImg } = mergeImageExtras(featBase, s3Class, wooClass);

  const wooGlass =
    (order.woo_glass_raw as Record<string, string> | undefined) ||
    ((order.metadata as Record<string, unknown> | undefined)?.woo_glass_raw as Record<string, string> | undefined) ||
    {};

  const gs1 = formatGlassSideDisplay(order, 'one') || trimStr(wooGlass.side_one);
  const gs2 = formatGlassSideDisplay(order, 'two') || trimStr(wooGlass.side_two);
  const gs3 = formatGlassSideDisplay(order, 'three') || trimStr(wooGlass.side_three);

  const sb1Exists =
    !!trimStr(order.spacer_bar_type) ||
    !!trimStr(order.spacer_bar_color) ||
    (order.spacer_bar_thickness_mm !== null &&
      order.spacer_bar_thickness_mm !== undefined &&
      trimStr(order.spacer_bar_thickness_mm) !== '');
  const sb2Exists =
    !!trimStr(order.spacer_bar2_type) ||
    !!trimStr(order.spacer_bar2_color) ||
    (order.spacer_bar2_thickness_mm !== null &&
      order.spacer_bar2_thickness_mm !== undefined &&
      trimStr(order.spacer_bar2_thickness_mm) !== '');

  const gs1Exists = !!gs1;
  const gs2Exists = !!gs2;
  const gs3Exists = !!gs3;

  const gs1Line = gs1Exists ? `<div>GS1: ${escapeHtml(gs1)}</div>` : '';
  const sb1Line = sb1Exists
    ? `<div>SB1: ${escapeHtml(
        formatSpacerTriple(
          displaySpacerPart(order.spacer_bar_type),
          displaySpacerPart(order.spacer_bar_color),
          trimStr(order.spacer_bar_thickness_mm)
        )
      )}</div>`
    : '';
  const gs3Line = gs3Exists ? `<div>GS3: ${escapeHtml(gs3)}</div>` : '';
  const gs2Line = gs2Exists ? `<div>GS2: ${escapeHtml(gs2)}</div>` : '';
  const sb2Line = sb2Exists
    ? `<div>SB2: ${escapeHtml(
        formatSpacerTriple(
          displaySpacerPart(order.spacer_bar2_type),
          displaySpacerPart(order.spacer_bar2_color),
          trimStr(order.spacer_bar2_thickness_mm)
        )
      )}</div>`
    : '';

  const hasAnyGlassLine = gs1Exists || sb1Exists || gs3Exists || gs2Exists || sb2Exists;

  const notes = trimStr(order.notes);
  const extrasPresent = !!(features || featuresImg);

  if (!notes && !hasAnyGlassLine && !extrasPresent) {
    return null;
  }

  const postcode = trimStr(order.delivery_postcode);
  const jobNo = trimStr(order.website_job_number);
  const headerRow = `<div class="header">${escapeHtml(postcode)} — ${escapeHtml(jobNo)}</div>`;
  const notesRow = notes ? `<div class="row"><strong>Ref:</strong> ${escapeHtml(notes)}</div>` : '';

  const gw = order.glass_width;
  const gh = order.glass_height;
  const diameter = order.diameter_mm;
  const gwStr = gw !== null && gw !== undefined ? String(gw).trim() : '';
  const ghStr = gh !== null && gh !== undefined ? String(gh).trim() : '';
  const diameterStr = diameter !== null && diameter !== undefined ? String(diameter).trim() : '';
  const dimensionsRow =
    order.round_glass && diameterStr
      ? `<div class="row-height-width"><strong>Ø${escapeHtml(diameterStr)}</strong> </div>`
      : gwStr || ghStr
      ? `<div class="row-height-width"><strong>${escapeHtml(gwStr)} x ${escapeHtml(ghStr)}</strong> </div>`
      : '';

  const glassInfoRow = hasAnyGlassLine
    ? `<div class="row">
             ${gs1Line}
             ${sb1Line}
             ${gs2Line}
             ${sb2Line}
             ${gs3Line}
           </div>`
    : '';

  const extrasRow =
    features || featuresImg
      ? `<div class="row"><strong>Extras:</strong> ${escapeHtml(features)}${
          featuresImg ? ' (' + escapeHtml(featuresImg) + ')' : ''
        }</div>`
      : '';

  const qty = order.quantity;
  const quantityRow =
    qty !== null && qty !== undefined && String(qty).trim() !== ''
      ? `<div class="row"><strong>Quantity:</strong> ${escapeHtml(String(qty))}</div>`
      : '';

  return `
    <html>
      <head>
        <style>
          @page {
            size: 100mm 56mm;
            margin: 0;
          }
          html, body {
            width: 100mm;
            height: 56mm;
            margin: 0;
            padding: 0;
            overflow: hidden;
          }
          .outer-label {
            width: 100mm;
            height: 56mm;
            display: flex;
            justify-content: center;
            align-items: center;
          }
          .label {
            width: 100mm;
            height: 56mm;
            padding: 1mm 1mm 1mm 13mm;
            box-sizing: border-box;
            font-family: Arial, sans-serif;
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: flex-start;
          }
          .row {
            margin-bottom: 1mm;
            font-size: 8pt;
            line-height: 1.2;
          }
          .header {
            font-weight: bold;
            text-align: center;
            margin-bottom: 1mm;
            font-size: 13pt;
          }
          .row-height-width{
            font-size: 10pt;
          }
        </style>
      </head>
      <body>
        <div class="outer-label">
          <div class="label">
            ${headerRow}
            ${notesRow}
            ${dimensionsRow}
            ${glassInfoRow}
            ${extrasRow}
            ${quantityRow}
          </div>
        </div>
      </body>
    </html>
  `;
}

/**
 * Print via a hidden iframe (same tab). Avoids Chrome popup blockers that block `window.open`
 * even on button clicks when the chain is async (e.g. after awaiting image fetch).
 *
 * @returns false only if the iframe/document could not be created
 */
export function printProductionLineLabel(html: string): boolean {
  if (typeof document === 'undefined' || !document.body) {
    return false;
  }

  const iframe = document.createElement('iframe');
  iframe.setAttribute('aria-hidden', 'true');
  iframe.setAttribute('title', 'Print label');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '100mm';
  iframe.style.height = '56mm';
  iframe.style.border = '0';
  iframe.style.opacity = '0';
  iframe.style.pointerEvents = 'none';
  iframe.style.zIndex = '-1';

  document.body.appendChild(iframe);

  const doc = iframe.contentDocument;
  const win = iframe.contentWindow;
  if (!doc || !win) {
    iframe.remove();
    return false;
  }

  doc.open();
  doc.write(html);
  doc.close();

  let cleaned = false;
  const cleanup = () => {
    if (cleaned) return;
    cleaned = true;
    iframe.remove();
  };

  const runPrint = () => {
    try {
      win.focus();
      win.print();
    } catch {
      cleanup();
      return;
    }
    win.addEventListener('afterprint', cleanup, { once: true });
    window.setTimeout(cleanup, 3000);
  };

  // Brief delay so @page / layout are applied (same idea as the old 100ms window.open path)
  win.requestAnimationFrame(() => {
    window.setTimeout(runPrint, 100);
  });

  return true;
}
